import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

export type FeedbackType = 'bug' | 'suggestion' | 'question' | 'other';
export type FeedbackStatus = 'new' | 'in_progress' | 'resolved' | 'closed';
export type FeedbackPriority = 'low' | 'medium' | 'high';

export interface Feedback {
  id: string;
  establishment_id?: string;
  user_id: string;
  user_email: string;
  user_name?: string;
  type: FeedbackType;
  subject: string;
  message: string;
  screenshot_path?: string;
  page_url?: string;
  browser_info?: Record<string, unknown>;
  status: FeedbackStatus;
  priority: FeedbackPriority;
  admin_notes?: string;
  assigned_to?: string;
  resolved_at?: string;
  created_at: string;
  updated_at: string;
  // Joined data
  establishment_name?: string;
}

export interface FeedbackFilters {
  type?: FeedbackType | 'all';
  status?: FeedbackStatus | 'all';
  priority?: FeedbackPriority | 'all';
  period?: '7d' | '30d' | '90d' | 'all';
  search?: string;
}

export interface FeedbackStats {
  total: number;
  newThisWeek: number;
  byType: Record<FeedbackType, number>;
  byStatus: Record<FeedbackStatus, number>;
}

export function useAdminFeedbacks() {
  const [feedbacks, setFeedbacks] = useState<Feedback[]>([]);
  const [stats, setStats] = useState<FeedbackStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState<FeedbackFilters>({
    type: 'all',
    status: 'all',
    priority: 'all',
    period: '30d',
    search: '',
  });
  const { toast } = useToast();

  const fetchFeedbacks = useCallback(async () => {
    try {
      setLoading(true);
      
      let query = supabase
        .from('feedbacks')
        .select(`
          *,
          establishments:establishment_id (name)
        `)
        .order('created_at', { ascending: false });

      // Apply filters
      if (filters.type && filters.type !== 'all') {
        query = query.eq('type', filters.type);
      }

      if (filters.status && filters.status !== 'all') {
        query = query.eq('status', filters.status);
      }

      if (filters.priority && filters.priority !== 'all') {
        query = query.eq('priority', filters.priority);
      }

      if (filters.period && filters.period !== 'all') {
        const now = new Date();
        let startDate: Date;
        
        switch (filters.period) {
          case '7d':
            startDate = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
            break;
          case '30d':
            startDate = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
            break;
          case '90d':
            startDate = new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000);
            break;
          default:
            startDate = new Date(0);
        }
        
        query = query.gte('created_at', startDate.toISOString());
      }

      if (filters.search) {
        query = query.or(`subject.ilike.%${filters.search}%,message.ilike.%${filters.search}%,user_email.ilike.%${filters.search}%`);
      }

      const { data, error } = await query;

      if (error) throw error;

      // Transform data to include establishment name
      const typedFeedbacks = (data || []).map(feedback => ({
        ...feedback,
        type: feedback.type as FeedbackType,
        status: feedback.status as FeedbackStatus,
        priority: feedback.priority as FeedbackPriority,
        establishment_name: feedback.establishments?.name || undefined,
      })) as Feedback[];

      setFeedbacks(typedFeedbacks);
    } catch (error) {
      console.error('Error fetching feedbacks:', error);
      toast({
        title: "Erreur",
        description: "Impossible de charger les feedbacks",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  }, [filters, toast]);

  const fetchStats = useCallback(async () => {
    try {
      const { data, error } = await supabase
        .from('feedbacks')
        .select('id, type, status, created_at');

      if (error) throw error;

      const now = new Date();
      const oneWeekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);

      const byType: Record<FeedbackType, number> = {
        bug: 0,
        suggestion: 0,
        question: 0,
        other: 0,
      };

      const byStatus: Record<FeedbackStatus, number> = {
        new: 0,
        in_progress: 0,
        resolved: 0,
        closed: 0,
      };

      let newThisWeek = 0;

      (data || []).forEach(feedback => {
        const type = feedback.type as FeedbackType;
        const status = feedback.status as FeedbackStatus;
        
        if (byType[type] !== undefined) {
          byType[type]++;
        }
        
        if (byStatus[status] !== undefined) {
          byStatus[status]++;
        }

        if (new Date(feedback.created_at) >= oneWeekAgo) {
          newThisWeek++;
        }
      });

      setStats({
        total: data?.length || 0,
        newThisWeek,
        byType,
        byStatus,
      });
    } catch (error) {
      console.error('Error fetching feedback stats:', error);
    }
  }, []);

  const updateFeedbackStatus = async (feedbackId: string, newStatus: FeedbackStatus) => {
    try {
      const updates: Record<string, unknown> = {
        status: newStatus,
        updated_at: new Date().toISOString(),
      };

      if (newStatus === 'resolved') {
        updates.resolved_at = new Date().toISOString();
      }

      const { error } = await supabase
        .from('feedbacks')
        .update(updates)
        .eq('id', feedbackId);

      if (error) throw error;

      toast({
        title: "Statut mis à jour",
        description: `Le feedback a été marqué comme "${getStatusLabel(newStatus)}"`,
      });

      await fetchFeedbacks();
      await fetchStats();
    } catch (error) {
      console.error('Error updating feedback status:', error);
      toast({
        title: "Erreur",
        description: "Impossible de mettre à jour le statut",
        variant: "destructive",
      });
    }
  };

  const updateFeedbackPriority = async (feedbackId: string, newPriority: FeedbackPriority) => {
    try {
      const { error } = await supabase
        .from('feedbacks')
        .update({ priority: newPriority, updated_at: new Date().toISOString() })
        .eq('id', feedbackId);

      if (error) throw error;

      toast({
        title: "Priorité mise à jour",
      });

      await fetchFeedbacks();
    } catch (error) {
      console.error('Error updating feedback priority:', error);
      toast({
        title: "Erreur",
        description: "Impossible de mettre à jour la priorité",
        variant: "destructive",
      });
    }
  };

  const updateFeedbackNotes = async (feedbackId: string, notes: string) => {
    try {
      const { error } = await supabase
        .from('feedbacks')
        .update({ admin_notes: notes, updated_at: new Date().toISOString() })
        .eq('id', feedbackId);

      if (error) throw error;

      toast({
        title: "Notes sauvegardées",
      });

      await fetchFeedbacks();
    } catch (error) {
      console.error('Error updating feedback notes:', error);
      toast({
        title: "Erreur",
        description: "Impossible de sauvegarder les notes",
        variant: "destructive",
      });
    }
  };

  const deleteFeedback = async (feedbackId: string) => {
    try {
      const { error } = await supabase
        .from('feedbacks')
        .delete()
        .eq('id', feedbackId);

      if (error) throw error;

      toast({
        title: "Feedback supprimé",
      });

      await fetchFeedbacks();
      await fetchStats();
    } catch (error) {
      console.error('Error deleting feedback:', error);
      toast({
        title: "Erreur",
        description: "Impossible de supprimer le feedback",
        variant: "destructive",
      });
    }
  };

  useEffect(() => {
    fetchFeedbacks();
    fetchStats();
  }, [fetchFeedbacks, fetchStats]);

  return {
    feedbacks,
    stats,
    loading,
    filters,
    setFilters,
    updateFeedbackStatus,
    updateFeedbackPriority,
    updateFeedbackNotes,
    deleteFeedback,
    refetch: fetchFeedbacks,
  };
}

export function getStatusLabel(status: FeedbackStatus): string {
  const labels: Record<FeedbackStatus, string> = {
    new: 'Nouveau',
    in_progress: 'En cours',
    resolved: 'Résolu',
    closed: 'Fermé',
  };
  return labels[status];
}

export function getTypeLabel(type: FeedbackType): string {
  const labels: Record<FeedbackType, string> = {
    bug: 'Bug',
    suggestion: 'Suggestion',
    question: 'Question',
    other: 'Autre',
  };
  return labels[type];
}

export function getPriorityLabel(priority: FeedbackPriority): string {
  const labels: Record<FeedbackPriority, string> = {
    low: 'Faible',
    medium: 'Moyenne',
    high: 'Haute',
  };
  return labels[priority];
}

export function getStatusColor(status: FeedbackStatus): string {
  const colors: Record<FeedbackStatus, string> = {
    new: 'bg-blue-500/10 text-blue-600 border-blue-500/20',
    in_progress: 'bg-amber-500/10 text-amber-600 border-amber-500/20',
    resolved: 'bg-green-500/10 text-green-600 border-green-500/20',
    closed: 'bg-gray-500/10 text-gray-600 border-gray-500/20',
  };
  return colors[status];
}

export function getTypeColor(type: FeedbackType): string {
  const colors: Record<FeedbackType, string> = {
    bug: 'bg-red-500/10 text-red-600 border-red-500/20',
    suggestion: 'bg-amber-500/10 text-amber-600 border-amber-500/20',
    question: 'bg-blue-500/10 text-blue-600 border-blue-500/20',
    other: 'bg-gray-500/10 text-gray-600 border-gray-500/20',
  };
  return colors[type];
}

export function getPriorityColor(priority: FeedbackPriority): string {
  const colors: Record<FeedbackPriority, string> = {
    low: 'bg-gray-500/10 text-gray-600 border-gray-500/20',
    medium: 'bg-blue-500/10 text-blue-600 border-blue-500/20',
    high: 'bg-red-500/10 text-red-600 border-red-500/20',
  };
  return colors[priority];
}
